import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AddressBook, Plus } from "@phosphor-icons/react";
import { aender, lies, schick, weg } from "../../api/client";
import type { Beteiligter, BeteiligterArt, Kontakt, Projekt } from "../../api/types";
import { Dialog, Feld, Meldung } from "../../components/Bausteine";
import { BETEILIGTER_ART } from "../../lib/format";
import { Auswahl } from "../../components/Auswahl";
import { useIch } from "../../App";
import { useKontakte } from "../Adressen";

export function useBeteiligte(projektId: string | undefined) {
  return useQuery({
    queryKey: ["beteiligte", projektId],
    queryFn: () => lies<Beteiligter[]>(`/projekte/${projektId}/beteiligte`),
    // `undefined`, solange eine Seite das Projekt erst aus dem Dokument erfaehrt
    // (Protokoll) — ohne die Bremse ging GET /projekte//beteiligte raus.
    enabled: !!projektId,
  });
}

type Form = Omit<Beteiligter, "id">;
const LEER: Form = { art: "auftragnehmer", firma: "", name: "", funktion: "", email: "", telefon: "", kuerzel: "", im_verteiler: true };

/** Beteiligte brauchen kein Konto (E4) — sie stehen in Teilnehmerlisten und Verteilern.
 *  Die Anschrift kommt aus dem zentralen Adressbuch (SPEC §16), Rolle, Kürzel und
 *  Verteiler gelten nur in diesem Projekt. */
export function BeteiligteReiter({ projekt, kann }: { projekt: Projekt; kann: boolean }) {
  const liste = useBeteiligte(projekt.id);
  const [bearbeiten, setBearbeiten] = useState<Beteiligter | "neu" | null>(null);
  return (
    <section className="karte">
      <div className="karte-kopf">
        <h2>Beteiligte</h2>
        <span className="mute">Teilnehmerlisten, Verteiler und „Wer“ im Protokoll</span>
        <div className="luft" />
        {kann && <button className="knopf haupt" onClick={() => setBearbeiten("neu")}><Plus size={16} weight="bold" /> Beteiligter</button>}
      </div>
      <Meldung fehler={liste.error} />
      <div className="liste">
        {liste.data?.map((b) => (
          <button key={b.id} className="zeile" disabled={!kann} onClick={() => setBearbeiten(b)}>
            <span className="nr">{b.kuerzel}</span>
            <div className="haupt">
              <div>{[b.firma, b.name].filter(Boolean).join(" · ")}</div>
              <div className="mute">{[BETEILIGTER_ART[b.art], b.funktion, b.email, b.telefon].filter(Boolean).join(" · ")}</div>
            </div>
            {b.im_verteiler ? <span className="chip akzent">Verteiler</span> : <span />}
          </button>
        ))}
      </div>
      {bearbeiten && <BeteiligterDialog projekt={projekt} b={bearbeiten === "neu" ? null : bearbeiten} zu={() => setBearbeiten(null)} />}
    </section>
  );
}

function BeteiligterDialog({ projekt, b, zu }: { projekt: Projekt; b: Beteiligter | null; zu: () => void }) {
  const qc = useQueryClient();
  const buero = ["admin", "buero"].includes(useIch().data?.rolle ?? "");
  const [f, setF] = useState<Form>(b ?? LEER);
  const [kontakt, setKontakt] = useState<Kontakt | null>(null);
  // Neu und im Büro: erst im Adressbuch suchen, dann (falls nicht da) neu eintippen.
  const [suche, setSuche] = useState("");
  const treffer = useKontakte(suche, !b && buero && suche.trim().length >= 2);
  // Leistungen zeigen den Auftragnehmer mit (`an`) — nach Aendern/Loeschen sonst veraltet.
  const fertig = () => {
    for (const k of [["beteiligte", projekt.id], ["leistungen", projekt.id], ["kontakte"]]) qc.invalidateQueries({ queryKey: k });
    zu();
  };
  const speichern = useMutation({
    mutationFn: () => b ? aender(`/projekte/${projekt.id}/beteiligte/${b.id}`, f)
                        : schick(`/projekte/${projekt.id}/beteiligte`, { ...f, kontakt_id: kontakt?.id ?? null }),
    onSuccess: fertig,
  });
  const entfernen = useMutation({ mutationFn: () => weg(`/projekte/${projekt.id}/beteiligte/${b!.id}`), onSuccess: fertig });
  const waehlen = (k: Kontakt) => {
    setKontakt(k);
    setF({ ...f, art: k.art, firma: k.firma, name: k.name, funktion: k.funktion, email: k.email, telefon: k.telefon });
    setSuche("");
  };
  // Aus dem Adressbuch: Anschrift dort ändern — Externe ändern sie gar nicht (SPEC §16.1).
  const gesperrt = !!kontakt || (!buero && !!b?.kontakt_id);
  const text = (k: keyof Form, titel: string, breit = false) => (
    <Feld titel={titel} breit={breit}>
      <input className="eingabe" value={String(f[k])} disabled={gesperrt && k !== "kuerzel"}
             onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </Feld>
  );
  return (
    <Dialog offen zu={zu} titel={b ? "Beteiligter" : "Neuer Beteiligter"} fuss={<>
      {b && <button className="knopf leise gefahr" onClick={() => confirm("Diesen Beteiligten aus dem Projekt entfernen? Im Adressbuch bleibt er. Ist er Auftragnehmer einer Leistung, steht sie danach ohne Auftragnehmer da.") && entfernen.mutate()}>Entfernen</button>}
      <div style={{ flex: 1 }} />
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={!(f.firma.trim() || f.name.trim()) || speichern.isPending} onClick={() => speichern.mutate()}>Speichern</button>
    </>}>
      {!b && buero && !kontakt && (
        <div className="adressbuch-suche">
          <label className="suche" style={{ width: "100%" }}>
            <AddressBook size={16} />
            <input className="eingabe" autoFocus placeholder="Im Adressbuch suchen – Firma, Name, E-Mail …" value={suche}
                   onChange={(e) => setSuche(e.target.value)} />
          </label>
          {suche.trim().length >= 2 && (
            <div className="ort-treffer">
              {treffer.data?.map((k) => {
                const schon = k.projekte.some((p) => p.id === projekt.id);
                return (
                  <button key={k.id} type="button" className="ort-zeile" disabled={schon} onClick={() => waehlen(k)}>
                    <AddressBook size={15} />
                    <span><b>{[k.firma, k.name].filter(Boolean).join(" · ")}</b>
                      <span className="mute"> · {[k.funktion, k.email, k.ort].filter(Boolean).join(" · ")}</span></span>
                    {schon && <span className="chip">schon beteiligt</span>}
                  </button>
                );
              })}
              {treffer.isSuccess && treffer.data.length === 0 && <div className="mute">Nicht im Adressbuch – unten neu eintragen.</div>}
            </div>
          )}
        </div>
      )}
      {!buero && b?.kontakt_id && (
        <p className="mute" style={{ marginTop: 0 }}>Die Anschrift pflegt das Büro im Adressbuch. Hier lassen sich Rolle, Kürzel und Verteiler ändern.</p>
      )}
      {kontakt && (
        <p className="mute" style={{ marginTop: 0 }}>Aus dem Adressbuch: {[kontakt.firma, kontakt.name].filter(Boolean).join(" · ")}.{" "}
          <button className="knopf leise" onClick={() => { setKontakt(null); setF(LEER); }}>anderen wählen</button></p>
      )}
      <div className="raster">
        <Feld titel="Rolle in diesem Projekt">
          <Auswahl value={f.art} onChange={(e) => setF({ ...f, art: e.target.value as BeteiligterArt })}>
            {Object.entries(BETEILIGTER_ART).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Auswahl>
        </Feld>
        {text("kuerzel", "Kürzel im Protokoll")}
        {text("firma", "Firma / Behörde", true)}
        {text("name", "Ansprechperson")}
        {text("funktion", "Funktion")}
        {text("email", "E-Mail")}
        {text("telefon", "Telefon")}
        <label className="haken breit"><input type="checkbox" checked={f.im_verteiler} onChange={(e) => setF({ ...f, im_verteiler: e.target.checked })} /> im Verteiler</label>
      </div>
      <p className="mute">{b
        ? "Firma, Name, Funktion, E-Mail und Telefon gelten im Adressbuch und in allen Projekten; Rolle, Kürzel und Verteiler nur hier. Freigegebene Protokolle behalten, was damals galt."
        : "Neu Eingetragenes landet im Adressbuch; gibt es die E-Mail dort schon, wird dieser Kontakt verwendet."}</p>
      <Meldung fehler={speichern.error ?? entfernen.error} />
    </Dialog>
  );
}
